import { useCapability } from '@app/shared/hooks/capability.hook';
import { useCurrentCashierShift } from '@app/shared/hooks/cashier.hook';
import { useHasPermission } from '@app/shared/hooks/permission.hook';
import { useShiftGateStore } from '@app/shared/store/shift-gate.store';
import { Loader2 } from 'lucide-react';
import { FC, ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { PRIVATE_PATH } from '../../constants/paths.constant';

interface ShiftGateGuardProps {
  children: ReactNode;
}

/**
 * ShiftGateGuard - Gate that forces users holding the
 * `shifts.gate_required` permission to open a shift before accessing
 * the app. Admins revoke the perm from a role → that role bypasses.
 *
 * Migrated from the legacy role-hardcoded SHIFT_EXEMPT_ROLES list so
 * admins can toggle the gate per-role from
 * /configuration/role-permissions without a code change.
 */
export const ShiftGateGuard: FC<ShiftGateGuardProps> = ({ children }) => {
  const location = useLocation();
  const setShiftGateState = useShiftGateStore(
    (state) => state.setShiftGateState
  );

  // Exempt when the user does NOT hold the gate-required perm, OR when
  // the shifts capability is resolved-absent (billing lapsed, or the
  // addon isn't owned). Perm alone is stale on billing lapse — migration
  // 0508 granted `shifts.gate_required` business-wide with no capability
  // awareness, so a past_due/suspended business kept the perm while the
  // API stopped honoring `shifts.enabled`, storming /shifts/current.
  const isGateRequired = useHasPermission('shifts.gate_required');
  const { hasFeature, isLoading: isCapLoading } = useCapability();
  const hasShiftsCapability = hasFeature('shifts.enabled');
  const isExempt = !isGateRequired || (!isCapLoading && !hasShiftsCapability);

  // Fetch current shift only for gated users who actually own the cap
  const {
    data: currentShift,
    isLoading,
    isFetching,
  } = useCurrentCashierShift({
    enabled: isGateRequired && !isCapLoading && hasShiftsCapability,
  });

  // The open-close-shifts page path
  const shiftGatePath = PRIVATE_PATH.OPEN_CLOSE_SHIFT;
  const isOnShiftGatePage = location.pathname === shiftGatePath;
  // Contract signing takes precedence over the shift gate — bypass so we
  // don't ping-pong with AuthGuard, which forces unsigned users to the
  // contract page.
  const isOnContractRoute = location.pathname.startsWith(
    PRIVATE_PATH.CONTRACT_SIGNING
  );

  // Update shift gate store based on current state
  useEffect(() => {
    if (isExempt) {
      // Exempt users always pass the gate
      setShiftGateState(true, null);
    } else if (currentShift) {
      // User has an active shift
      setShiftGateState(true, currentShift.id);
    } else if (!isLoading && !isFetching) {
      // User doesn't have an active shift
      setShiftGateState(false, null);
    }
  }, [isExempt, currentShift, isLoading, isFetching, setShiftGateState]);

  // Show loading state while checking capability or shift status
  if (isGateRequired && (isCapLoading || (!isExempt && isLoading))) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Exempt users can access everything
  if (isExempt) {
    return children;
  }

  // Contract signing must run before the shift gate
  if (isOnContractRoute) {
    return children;
  }

  // Non-exempt users without an active shift
  if (!currentShift) {
    // Allow access to the shift gate page
    if (isOnShiftGatePage) {
      return children;
    }

    // Redirect to shift gate page for all other routes
    return <Navigate to={shiftGatePath} replace />;
  }

  // User has an active shift, allow access
  return children;
};

export default ShiftGateGuard;
